import { SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'usehooks-ts'

import { useAuth } from '@features/auth'
import { CartProduct, useCartStore } from '@features/cart/model/useCartStore'
import { useErrorInfoModal } from '@features/modal'

import useProductsSimilarModal from '@widgets/products-similar/hooks/useProductsSimilarModal'

import { Product, useDropCartItemMutation } from '@shared/generated'
import { useAnalytics } from '@shared/lib/analytics'

import CartPosition from './cart-position'
import CartPositionMobile from './cart-position.mobile'

import styles from './styles.module.scss'

interface CartListPreviewProps {
  data: Product[];
}

const CartPositionComponents = {
  mobile: CartPositionMobile,
  default: CartPosition
}

export default function CartListPreview (props: CartListPreviewProps) {
  const { t: te } = useTranslation('errors')
  const { data } = props
  const { getProduct, removeProduct } = useCartStore()
  const { isAuthorized } = useAuth()
  const { openProductsSimilarModal } = useProductsSimilarModal()
  const [ fetchRemoveProduct ] = useDropCartItemMutation()
  const { currentShopId } = useCartStore()
  const { openErrorInfoModal } = useErrorInfoModal()
  const { removeFromCart: trackRemoveFromCart } = useAnalytics()

  const isTablet = useMediaQuery('(max-width: 768px)')

  const componentTypeToRender = isTablet ? 'mobile' : 'default'

  const CartLineComponent = CartPositionComponents[componentTypeToRender] ?? CartPositionComponents['mobile']

  const removeCartPosition = async (productId: string) => {
    const product = data.find((p) => p.id === productId)
    const cartItem = getProduct(productId)

    if (product) {
      trackRemoveFromCart(product, cartItem?.amount ?? 1)
    }

    if (isAuthorized) {
      try {
        await fetchRemoveProduct({
          variables: {
            shopId: currentShopId as string,
            productId: productId,
          },
        })

        removeProduct(productId)
      }
      catch (error) {
        console.error('fetchRemoveProduct error: ', error)
        openErrorInfoModal({
          description: te('drop_cart_item'),
        })
      }
    }
    else {
      removeProduct(productId)
    }
  }

  // Event handlers
  const handleOutOfStockPositionClick = (e: SyntheticEvent, _item: CartProduct | undefined, product: Product) => {
    e.preventDefault()
    openProductsSimilarModal(product.id)
  }

  const handleRemoveClick = (e: SyntheticEvent | null, productId: string) => {
    if (e) {
      e.preventDefault()
    }
    removeCartPosition(productId)
  }

  return (
    <div className={styles.list}>
      {
        data.map((product) => {
          return <CartLineComponent
            key={product.id}
            item={getProduct(product.id)}
            product={product}
            onOutOfStockPositionClick={handleOutOfStockPositionClick}
            onRemoveRequested={handleRemoveClick}
          />
        })
      }
    </div>
  )
}
